import { ReactNode, useEffect, useState } from 'react';

import AppNotifications from 'src/components/app-notifications';
import AssignmentsList from 'src/components/assignments/assignments-list';
import { NoAssignmentDataView } from 'src/components/assignments/no-assignment-data-view';
import { LoadingScreen } from 'src/components/common/loaders';
import { useMSAssignmentListData } from 'src/hooks/assignment-list-data';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import useEffectOnce from 'src/hooks/use-effect-once.hook';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll.hook';
import { usePrevious } from 'src/hooks/use-previous.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import authService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  setAssignmentsTotalCount,
  setDisplayedAssignmentsCount,
  setDoneAssignmentsCount,
  useActiveMSAssignmentsStatus,
  useActiveMSAssignmentsSubject,
  useIsNoAssignmentsDataViewMSSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import { ColumnsHeadings } from '../columns-headings';

// @TODO: remove after RFS
export const MsAssignments = () => {
  const isStudent = authService.isStudent();

  const isNoAssignmentsDataViewMS = useIsNoAssignmentsDataViewMSSelector();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const dispatch = useAppDispatch();

  const {
    itemsToDisplay,
    allAssignmentCount,
    incrementItemsToDisplayCount,
    isLoading,
    isFetching,
  } = useMSAssignmentListData();
  const { isLoadMoreRefVisible, setLoadMoreRef } = useInfiniteScroll();

  const activeMSAssignmentsStatus = useActiveMSAssignmentsStatus();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();

  const prevMSAssignmentsStatus = usePrevious(activeMSAssignmentsStatus);
  const prevMSAssignmentsSubject = usePrevious(activeMSAssignmentsSubject);

  const [
    isMSAssignmentsFetchingOnFilterChange,
    setIsMSAssignmentsFetchingOnFilterChange,
  ] = useState(false);

  // Set whether student assignments are being fetched after filters changed to show the loading screen.
  useEffect(() => {
    const isFetchingAfterFilterChange =
      (activeMSAssignmentsSubject !== prevMSAssignmentsSubject ||
        activeMSAssignmentsStatus !== prevMSAssignmentsStatus) &&
      isFetching &&
      isStudent;

    setIsMSAssignmentsFetchingOnFilterChange(isFetchingAfterFilterChange);
    // Avoid including prev states in deps as we only track the active ones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeMSAssignmentsStatus,
    activeMSAssignmentsSubject,
    isFetching,
    isStudent,
  ]);

  // Set assignments xAPI payload data.
  useEffect(() => {
    dispatch(setDisplayedAssignmentsCount(itemsToDisplay.length ?? 0));
    dispatch(setAssignmentsTotalCount(allAssignmentCount ?? 0));
    dispatch(setDoneAssignmentsCount(0));
  }, [allAssignmentCount, dispatch, itemsToDisplay.length]);

  useEffect(() => {
    if (isLoadMoreRefVisible) {
      incrementItemsToDisplayCount();
    }
  }, [isLoadMoreRefVisible, incrementItemsToDisplayCount]);

  useEffectOnce(() => {
    eventService.send(XApiEventKeys.viewed, undefined, {
      result: {
        isRightClick: false,
        ...commonXapiContextPayload,
      },
    });
  }, [itemsToDisplay]);

  const wrapper = (children: ReactNode) => {
    return (
      <>
        <AppNotifications />
        <>{children}</>
      </>
    );
  };

  if (isLoading || isMSAssignmentsFetchingOnFilterChange) {
    return wrapper(
      isStudent ? <LoadingScreen isTransparentVersion={true} /> : null,
    );
  }

  if (!itemsToDisplay) {
    return wrapper(null);
  }

  if (isNoAssignmentsDataViewMS || !itemsToDisplay.length) {
    return wrapper(<NoAssignmentDataView isMiddleSchoolView={true} />);
  }

  return wrapper(
    <>
      <ColumnsHeadings />
      <AssignmentsList assignments={itemsToDisplay} />
      <div ref={setLoadMoreRef} />
    </>,
  );
};
